import {
  RISK_ESTIMATE_STATE_FILTER_VALUES,
  type ExtensionRiskEstimate,
} from '@kirin/api-client/contracts/extensions';

/** Risk text for an extension with no AgentMesh assessment. */
export const NOT_ASSESSED_LABEL = 'Not assessed';

/** Risk text for a built-in — deliberately not a verdict (never "Safe"). */
export const NOT_SCANNED_BUILT_IN_LABEL = 'Not scanned — built-in';

/**
 * Why a cell reads "Not assessed" — distinct from AgentMesh's own
 * "Unscanned" (AgentMesh knows the extension but has not scanned it).
 */
export const NOT_ASSESSED_DESCRIPTION =
  'AgentMesh has no result for this extension yet';

/** Why a built-in reads "Not scanned — built-in" (matches the expanded row). */
export const NOT_SCANNED_BUILT_IN_DESCRIPTION =
  'Ships inside the IDE, so AgentMesh does not scan it';

/**
 * Best-effort mapping of known AgentMesh vocabulary onto the Alerts severity
 * badge palette (KIR-1928). AgentMesh strings are displayed as-is; this map
 * only picks the badge color. Unknown vocabulary falls through to
 * SeverityBadge's neutral 'secondary' variant, so new AgentMesh values render
 * without a code change (uncolored until mapped here).
 */
const KNOWN_RISK_LABEL_SEVERITY: Record<string, string> = {
  critical: 'critical',
  malicious: 'critical',
  high: 'high',
  high_risk: 'high',
  medium: 'medium',
  suspicious: 'medium',
  risky: 'medium',
  low: 'low',
  low_risk: 'low',
  clean: 'low',
};

/**
 * The displayed label: AgentMesh level, falling back to status. Trims BEFORE
 * coalescing — same order as the SQL rollup's coalesce(nullif(trim(...))) —
 * so a whitespace-only level falls through to the status label instead of
 * rendering "Not assessed" for a row that sorts/filters as its status.
 */
export function riskEstimateLabel(
  riskEstimate: ExtensionRiskEstimate | undefined
): string | null {
  const level = riskEstimate?.level?.trim();
  const status = riskEstimate?.status?.trim();
  return level || status || null;
}

/** Badge color for a raw AgentMesh label; unknown → neutral 'secondary'. */
export function riskLabelSeverity(label: string): string {
  return KNOWN_RISK_LABEL_SEVERITY[label.toLowerCase()] ?? 'secondary';
}

/** 'high_risk' → 'High Risk' — human form of a raw AgentMesh label. */
export function formatRiskLabel(label: string): string {
  return label
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Risk filter option label: the text its cell shows — "Not assessed" /
 * "Not scanned — built-in" for the label-less states (KIR-4726), otherwise
 * the formatted AgentMesh label.
 */
export function riskFilterOptionLabel(value: string): string {
  if (value === RISK_ESTIMATE_STATE_FILTER_VALUES.NOT_ASSESSED) {
    return NOT_ASSESSED_LABEL;
  }
  if (value === RISK_ESTIMATE_STATE_FILTER_VALUES.NOT_SCANNED_BUILT_IN) {
    return NOT_SCANNED_BUILT_IN_LABEL;
  }
  return formatRiskLabel(value);
}

/**
 * Whether an extension without a result reads "Not scanned — built-in":
 * built-in, and no version installed from the marketplace — such a version
 * is scanned, so the extension reads "Not assessed" until its result lands
 * (KIR-4726). Same rule as the backend's Risk filter states.
 */
export function readsAsBuiltIn(extension: {
  isBuiltIn: boolean;
  versions: readonly { isBuiltIn?: boolean }[];
}): boolean {
  return (
    extension.isBuiltIn && !extension.versions.some(v => v.isBuiltIn === false)
  );
}
